import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useLang, LANGS } from "../i18n/LanguageContext";
import Logo from "./Logo";

function LangSwitcher({ compact = false }) {
  const { lang, setLang } = useLang();
  return (
    <div className={`flex items-center gap-1 ${compact ? "" : "border border-pine/15 px-1 py-0.5"}`} role="group" aria-label="Lingua / Language" data-testid="lang-switcher">
      {LANGS.map((l) => (
        <button
          key={l.code}
          onClick={() => setLang(l.code)}
          className={`px-2 py-1 text-[0.68rem] font-semibold uppercase tracking-[0.12em] transition-colors duration-300 ${
            lang === l.code ? "text-gold" : "text-ink-muted hover:text-pine"
          }`}
          aria-pressed={lang === l.code}
          data-testid={`lang-${l.code}`}
        >
          {l.label}
        </button>
      ))}
    </div>
  );
}

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();

  const LINKS = [
    { to: "/", label: t("nav_home"), testid: "nav-link-home" },
    { to: "/liberty", label: t("nav_liberty"), testid: "nav-link-liberty" },
    { to: "/partner", label: t("nav_partner"), testid: "nav-link-partner" },
    { to: "/contatti", label: t("nav_contatti"), testid: "nav-link-contatti" },
  ];

  const linkClass = ({ isActive }) =>
    `relative text-sm font-sans font-medium uppercase tracking-[0.16em] transition-colors duration-300 after:absolute after:-bottom-1.5 after:start-0 after:h-px after:bg-gold after:transition-[width] after:duration-300 ${
      isActive ? "text-pine after:w-full" : "text-ink/70 hover:text-pine after:w-0 hover:after:w-full"
    }`;

  return (
    <>
    <header className="sticky top-0 z-50 border-b border-pine/10 bg-surface/85 backdrop-blur-md" data-testid="main-header">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 h-20 flex items-center justify-between gap-4">
        <Link to="/" data-testid="nav-brand" aria-label="Liberty CP — Home">
          <Logo />
        </Link>
        <nav className="hidden lg:flex items-center gap-8" aria-label="Navigazione principale">
          {LINKS.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.to === "/"} className={linkClass} data-testid={l.testid}>
              {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="hidden lg:flex items-center gap-3">
          <LangSwitcher />
          {user ? (
            <>
              {user.role === "admin" && (
                <Link to="/admin" className="btn-outline !px-5 !py-2.5" data-testid="nav-admin-button">{t("nav_admin")}</Link>
              )}
              <Link to="/area" className="btn-primary !px-5 !py-2.5" data-testid="nav-area-button">{t("nav_area")}</Link>
            </>
          ) : (
            <>
              <Link to="/accedi" className="btn-outline !px-5 !py-2.5" data-testid="nav-login-button">{t("nav_accedi")}</Link>
              <Link to="/accedi?mode=registrati" className="btn-primary !px-5 !py-2.5" data-testid="nav-request-access-button">
                {t("nav_richiedi")}
              </Link>
            </>
          )}
        </div>
        <div className="lg:hidden flex items-center gap-3">
          <LangSwitcher compact />
          <button
            className="p-2 text-pine"
            onClick={() => setOpen(true)}
            aria-label="Apri menu"
            data-testid="nav-menu-open"
          >
            <Menu size={26} />
          </button>
        </div>
      </div>
    </header>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[60] bg-cream flex flex-col"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            data-testid="mobile-menu"
          >
            <div className="h-20 px-6 flex items-center justify-between border-b border-pine/10">
              <Link to="/" data-testid="nav-brand-mobile" aria-label="Liberty CP — Home" onClick={() => setOpen(false)}>
                <Logo />
              </Link>
              <button className="p-2 text-pine" onClick={() => setOpen(false)} aria-label="Chiudi menu" data-testid="nav-menu-close">
                <X size={26} />
              </button>
            </div>
            <nav className="flex-1 flex flex-col justify-center gap-2 px-8" aria-label="Navigazione mobile">
              {LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.5 }}
                >
                  <NavLink
                    to={l.to}
                    end={l.to === "/"}
                    onClick={() => setOpen(false)}
                    className="block py-3 font-serif text-4xl text-ink hover:text-pine transition-colors"
                    data-testid={`mobile-${l.testid}`}
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
            </nav>
            <div className="px-8 pb-10 flex flex-col gap-3">
              {user ? (
                <button
                  className="btn-primary w-full"
                  data-testid="mobile-nav-area-button"
                  onClick={() => { setOpen(false); navigate("/area"); }}
                >
                  {t("nav_area")}
                </button>
              ) : (
                <>
                  <button
                    className="btn-outline w-full"
                    data-testid="mobile-nav-login-button"
                    onClick={() => { setOpen(false); navigate("/accedi"); }}
                  >
                    {t("nav_accedi")}
                  </button>
                  <button
                    className="btn-primary w-full"
                    data-testid="mobile-nav-request-access-button"
                    onClick={() => { setOpen(false); navigate("/accedi?mode=registrati"); }}
                  >
                    {t("nav_richiedi")}
                  </button>
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
